import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getMostRecentLocations } from 'emergency_data/src/utils/getMostRecentLoc';
import { INTERNATIONAL_SUPPORT } from '@rsos/capstone/src/constants/capabilities';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { SOURCE_SMS } from '@rsos/constants/locationSources';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import { isCallerIDFromOrg } from '@rsos/utils/isCallerIDFromOrg';
import useIsIRP from '@rsos/utils/useIsIRP';
import InfoWindowContent from '../InfoWindow/InfoWindowContent';
import { ACTIVE_MARKER_OPACITY, getIconsUrl } from '../Markers/Markers';
import {
  trackHoverOnPin,
  trackHoverOnPinWithTime,
} from '../SpatialLayers/spatialLayersTrackings';
import AllCallMarker from '../assets/allcall_marker.svg';
import AllCallMarkerHover from '../assets/allcall_marker_hover.svg';
import AllCallSMS from '../assets/allcall_textto911.svg';
import AllCallSMSHover from '../assets/allcall_textto911_hover.svg';
import { MOST_RECENT_LOC_ZINDEX } from '../constants/mapZIndexes';
import {
  clearInstanceListeners,
  createGoogleInfoWindow,
  createGoogleMarker,
  renderIntoInfoWindow,
} from '../utils';
import { isLocationInPolygon } from '../utils/spatialLayersHelpers';

/**
 * Handle drawing locations in CRG Expanded Map Card. Treated as all calls view
 * @param {Object} activeCrgObject
 * @param {Object} activeCrgObject.spatialObject - the current active spatial object
 * @param {Object} activeCrgObject.activeFloor - data for the current active floor
 * @param {Object} activeCrgObject.deeplink - deeplink data for the floors
 * @param {Boolean} activeCrgObject.deeplink.loading - loading state
 * @param {Object} activeCrgObject.deeplink.error
 * @param {Object} activeCrgObject.deeplink.data - data pertaining to the CRG building floors
 * @param {Instance} map - Google Maps instance
 * @param {String} removedID - ID of removed Alert or Call
 */
const CrgLocationMarkers = ({ activeCrgObject, map, removedID }) => {
  const [markers, setMarkers] = useState([]);
  const [infoWindows, setInfoWindows] = useState([]);
  const [hoveredID, setHoveredID] = useState(null);
  const [timer, setTimer] = useState(0);
  const clearHoveredTimer = useRef(null);

  const { numberWithNewLocation, selectedCallerID } = useSelector(
    state => state.emergencyCalls,
  );

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );

  const callQueue = useSelector(state =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );
  const isInternational = useSelector(
    state =>
      !!(
        state.psaps.currentPSAP.activeCapabilities &&
        state.psaps.currentPSAP.activeCapabilities[INTERNATIONAL_SUPPORT]
      ),
  );
  const { irpVersion } = useIsIRP();

  const getCallsInPolygon = () => {
    return Object.keys(callQueue)
      .filter(call => {
        const {
          mostRecentLocation,
          mostRecentSecondaryLocation,
        } = getMostRecentLocations(callQueue[call]);
        const location = mostRecentLocation || mostRecentSecondaryLocation;
        const coordinates = [
          parseFloat(location.longitude),
          parseFloat(location.latitude),
        ];
        // If `map_area` is falsy, the spatial object doesn't have phase 2 data
        return (
          activeCrgObject?.activeFloor?.map_area &&
          !!isLocationInPolygon(
            activeCrgObject?.activeFloor?.map_area,
            coordinates,
          )
        );
      })
      .map(phoneNumber => {
        const {
          mostRecentLocation,
          mostRecentSecondaryLocation,
        } = getMostRecentLocations(callQueue[phoneNumber]);
        const location = mostRecentLocation || mostRecentSecondaryLocation;

        return location;
      });
  };

  const filteredCalls = getCallsInPolygon();

  const isCallerIdInFilteredCalls = !!filteredCalls.find(
    call => call.caller_id === selectedCallerID,
  );
  const isConnectedCar =
    isCallerIdInFilteredCalls &&
    isCallerIDFromOrg('sxm', callQueue, selectedCallerID);

  const drawMarker = call => {
    // getIconsUrl taken from Markers.js
    const iconUrl = getIconsUrl({
      incident: call,
      incidentIdx: 0, // each call is the most recent location for a call
      incidentType: EMERGENCY_CALLS,
      isAllView: true, //
    });
    const markerOpts = {
      map,
      position: {
        lat: parseFloat(call.latitude),
        lng: parseFloat(call.longitude),
      },
      ...(iconUrl
        ? {
            icon: {
              url: iconUrl,
            },
          }
        : {}),
      opacity: ACTIVE_MARKER_OPACITY,
      incidentType: EMERGENCY_CALLS,
      rsosID: call.caller_id,
      zIndex: MOST_RECENT_LOC_ZINDEX,
    };
    // No marker circles to match main functionality
    return createGoogleMarker(markerOpts);
  };

  const drawMarkers = () => {
    const markers = filteredCalls.map(call => {
      return drawMarker(call);
    });
    setMarkers(markers);
    const infoWindows = markers.map((marker, idx) => {
      return handleMarkerReady({ call: filteredCalls[idx], marker });
    });
    setInfoWindows(infoWindows);
  };

  const clearMarker = marker => {
    marker.setMap(null);
    clearInstanceListeners(marker);
  };

  const clearMarkers = () => {
    if (markers) {
      markers.forEach(marker => {
        clearMarker(marker);
      });
      setMarkers([]);
    }
    if (infoWindows) {
      infoWindows.forEach(infoWindow => {
        infoWindow.setMap(null);
        clearInstanceListeners(infoWindow);
      });
      setInfoWindows([]);
    }
  };

  const handleMarkerReady = ({ call, marker }) => {
    const events = {
      onClick: () => {},
      onMouseEnter: () => handleMouseEnter({ call, marker }),
      onMouseLeave: () => handleMouseLeave({ call, marker }),
    };
    const eventMapping = {
      onClick: 'click',
      onMouseEnter: 'mouseover',
      onMouseLeave: 'mouseout',
    };
    Object.keys(events).forEach(eventName => {
      marker.addListener(eventMapping[eventName], () => {
        events[eventName](marker);
      });
    });
    const { caller_id } = call;
    const infoWindowOpts = {
      content: `<div id="emergencycall-${caller_id}" class="default-infoWindow"/>`,
      incidentType: EMERGENCY_CALLS,
      rsosID: call.caller_id,
      disableAutoPan: true,
    };
    const infoWindow = createGoogleInfoWindow(infoWindowOpts);
    const callContent = (
      <InfoWindowContent
        type={EMERGENCY_CALLS}
        data={call}
        isInternational={isInternational}
      />
    );
    renderIntoInfoWindow(`emergencycall-${caller_id}`, infoWindow, callContent);

    marker.addListener('mouseover', () => {
      infoWindow.open(map, marker);
    });
    marker.addListener('mouseout', () => {
      infoWindow.close(map, marker);
    });
    return infoWindow;
  };

  const handleMouseEnter = ({ call, marker }) => {
    setHoveredID(call.caller_id);
    trackHoverOnPin(irpVersion);
    if (call.source === SOURCE_SMS) {
      marker.setIcon(AllCallSMSHover);
    } else {
      marker.setIcon(AllCallMarkerHover);
    }
  };

  const handleMouseLeave = ({ call, marker }) => {
    setHoveredID(null);
    if (call.source === SOURCE_SMS) {
      marker.setIcon(AllCallSMS);
    } else {
      marker.setIcon(AllCallMarker);
    }
  };

  useEffect(() => {
    if (hoveredID) {
      if (timer < 2) {
        clearHoveredTimer.current = setTimeout(
          () => setTimer(timer => timer + 1),
          1000,
        );
      }
      if (timer >= 1 && timer < 2) {
        trackHoverOnPinWithTime(1, isConnectedCar, irpVersion);
      }
      if (timer >= 2) {
        trackHoverOnPinWithTime(2, isConnectedCar, irpVersion);
      }
    } else {
      setTimer(0);
    }
    return () => {
      clearTimeout(clearHoveredTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredID, timer]);

  // Remove marker for a call that has been removed from the queue
  useEffect(() => {
    const marker = markers.find(marker => marker.rsosID === removedID);
    if (marker) {
      marker.setMap(null);
      clearInstanceListeners(marker);
      const newMarkers = markers.filter(marker => marker.rsosID !== removedID);
      setMarkers(newMarkers);
    }
    const infoWindow = infoWindows.find(
      infoWindow => infoWindow.rsosID === removedID,
    );
    if (infoWindow) {
      infoWindow.setMap(null);
      clearInstanceListeners(infoWindow);
      const newInfoWindows = infoWindows.filter(
        infoWindow => infoWindow.rsosID !== removedID,
      );
      setMarkers(newInfoWindows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removedID]);

  // Draw incoming locations
  useEffect(() => {
    if (numberWithNewLocation) {
      const currentCall = filteredCalls.find(
        call => call.caller_id === numberWithNewLocation,
      );
      const currentMarker = markers.find(
        marker => marker.rsosID === numberWithNewLocation,
      );
      if (currentCall) {
        if (currentMarker) {
          // Update position
          const latlng = new window.google.maps.LatLng(
            Number(currentCall.latitude),
            Number(currentCall.longitude),
          );
          currentMarker.setPosition(latlng);
        } else {
          // Draw new marker
          const marker = drawMarker(currentCall);
          setMarkers([...markers, marker]);
          const infoWindow = handleMarkerReady({ call: currentCall, marker });
          setInfoWindows([...infoWindows, infoWindow]);
        }
      } else if (currentMarker && !currentCall) {
        // Call has moved out of the spatial object. Remove the marker
        clearMarker(currentMarker);
        setMarkers(
          markers.filter(marker => marker.rsosID !== numberWithNewLocation),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberWithNewLocation]);

  // Clear all markers when removing all calls
  useEffect(() => {
    if (!filteredCalls.length && markers.length) {
      clearMarkers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCalls.length]);

  // This will draw markers on initial render and when switching floors
  useEffect(() => {
    if (filteredCalls.length && !markers.length) {
      drawMarkers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCrgObject.activeFloor]);

  useEffect(() => {
    return () => {
      clearMarkers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default CrgLocationMarkers;
